import type * as React from 'react';

import { Block, type Document, type Fragment, type Inline, type Text } from '@gitbook/slate';
import { useLazyRef, useViewport } from '@gitbook/util-react-hooks';
import { useVirtualizedList } from '@gitbook/util-react-virtual';

import type { EditorContainer } from '../interfaces';

/** Default height for nodes when none can be computed by estimateNodeSize */
const NODE_DEFAULT_HEIGHT = 50;

/**
 * Vitualized node list controller.
 */
export interface VirtualizedNodeList {
    renderNodes: (
        parent: Document | Fragment | Block,
        renderNode: (
            node: Block | Inline | Text,
            vNode: VirtualizedNodeProps,
            index: number
        ) => React.ReactNode
    ) => React.ReactNode[];
}

/**
 * Current set of nodes being rendered.
 */
export type VirtualizedNodeListChunk = Set<string>;

/**
 * Props to render a virtual node.
 */
export interface VirtualizedNodeProps {
    /** React reference to access the DOM element. */
    ref?: React.RefCallback<Element>;

    /** True if the node should be rendered as a virtual one. */
    virtual: boolean;

    /** Height of the virtual placeholder to render */
    virtualHeight: number;
}

/**
 * Size of a node stored in the state.
 */
export interface VirtualizedNode {
    element: Element | null;

    /** Size of the node measured with the DOM */
    height?: number;

    /** Size of the node estimated before its rendering */
    estimatedHeight: number;
}

/**
 * Virtualize the rendering of a node list by only rendering nodes that are within the viewport.
 */
export function useVirtualizedNodeList({
    editor,
    nodeList,
    parentRef = editor.elementRef,
    containmentEnabled = true,
    virtualizationEnabled = true,
    viewportOptions = {},
}: {
    nodeList: Block[] | (Inline | Text)[];
    editor: EditorContainer;
    parentRef?: React.RefObject<Element | undefined>;
    virtualizationEnabled?: boolean;
    containmentEnabled?: boolean;
    viewportOptions?: {
        margin?: string;
        edgeVirtualWindow?: number;
    };
}): VirtualizedNodeList {
    const { document } = editor.value;

    /** State for each rendered node */
    const virtualNodesRef = useLazyRef(() => new WeakMap<Block, VirtualizedNode>());
    // biome-ignore lint/correctness/useHookAtTopLevel: support running in non-browser env for tests
    const viewport = typeof window === 'undefined' ? { height: 150 } : useViewport();
    const isBlockNodeList = nodeList.every((node) => Block.isBlock(node));
    const virtualizedElements = useVirtualizedList<{
        node: Block;
    }>({
        enabled: isBlockNodeList && virtualizationEnabled,
        parentRef,
        getEstimatedItemHeight: (item) => {
            const vNode = getVirtualNode(editor, item.node, document, virtualNodesRef.current);
            return vNode.height || vNode.estimatedHeight;
        },
        list: isBlockNodeList
            ? nodeList.map((el) => ({
                  key: el.key,
                  node: el,
              }))
            : [],
        edgeVirtualWindow: viewportOptions?.edgeVirtualWindow ?? viewport.height,
        viewportMargin: viewportOptions?.margin,
    });

    //
    // Callback to render the nodes
    //
    const renderNodes = (
        parent: Block | Document,
        renderNode: (
            node: Block | Inline | Text,
            vNode: VirtualizedNodeProps,
            index: number
        ) => React.ReactNode
    ) => {
        if (!virtualizationEnabled || !isBlockNodeList) {
            return nodeList.map((node, index) => {
                // If virtualized is disabled but containment is enabled, we'll need to still
                // estimate the height of the node so as not to interfere with scrolling.
                const vNode =
                    containmentEnabled && Block.isBlock(node)
                        ? getVirtualNode(editor, node, parent, virtualNodesRef.current)
                        : null;

                return renderNode(
                    node,
                    {
                        virtual: false,
                        virtualHeight: vNode ? vNode.height || vNode.estimatedHeight : 0,
                    },
                    index
                );
            });
        }

        return virtualizedElements.map(([item, visible, height], index) => {
            const canBeVirtualized = editor.stack.find('shouldVirtualizeNode', item.node);

            const vNodeProps: VirtualizedNodeProps = {
                ref: item.nodeRef,
                virtual: canBeVirtualized === false ? false : !visible,
                virtualHeight: height,
            };

            return renderNode(item.node, vNodeProps, index);
        });
    };

    return { renderNodes };
}

/**
 * Lazy initialize a virtual node for a given slate node.
 */
function getVirtualNode(
    editor: EditorContainer,
    node: Block,
    parent: Block | Document = editor.value.document,
    cache: WeakMap<Block, VirtualizedNode> = new WeakMap()
): VirtualizedNode {
    const virtualNode = cache.get(node) || {
        element: null,
        estimatedHeight: (editor.stack.find('estimateNodeSize', node, parent)?.height ||
            NODE_DEFAULT_HEIGHT) as number,
    };
    cache.set(node, virtualNode);

    return virtualNode;
}
